import { computeObjectAABB, getBox, getScaleCoefficient } from "../utils/auto-box-collider";
import {
  resolveUrl,
  fetchContentType,
  getDefaultResolveQuality,
  injectCustomShaderChunks,
  addMeshScaleAnimation,
  closeExistingMediaMirror
} from "../utils/media-utils";
import {
  isNonCorsProxyDomain,
  guessContentType,
  proxiedUrlFor,
  isHubsRoomUrl,
  isLocalHubsSceneUrl,
  isLocalOrPersistenceAvatarUrl
} from "../utils/media-url-utils";
import { addAnimationComponents } from "../utils/animation";
import configs from "../utils/configs";

import { SOUND_MEDIA_LOADING, SOUND_MEDIA_LOADED } from "../systems/sound-effects-system";
import { loadModel } from "./gltf-model-plus";
import { cloneObject3D, setMatrixWorld } from "../utils/three-utils";
import { waitForDOMContentLoaded } from "../utils/async-utils";

import { SHAPE } from "three-ammo/constants";
import { addComponent, entityExists, removeComponent } from "bitecs";
import { MediaContentBounds, MediaLoading } from "../bit-components";

import { createArtwork } from "../utils/custom-utils";
import { isPersistenceUrl } from "../utils/persistence-url-utils";
import { readArtworkRuntimeMetadata } from "../utils/canonical-asset-metadata";
import notificationsCenter from "../singleton/NotificationsCenter";
import { v4 as uuidv4 } from "uuid";

let loadingObjectSrc = null;
loadingObjectSrc = require(`../assets/models/LoadingObject_app.glb`);

let loadingObject;

waitForDOMContentLoaded().then(() => {
  loadModel(loadingObjectSrc).then(gltf => {
    loadingObject = gltf;
  });
});
AFRAME.registerComponent("media-loader", {
  schema: {
    playSoundEffect: { default: true },
    fileId: { type: "string" },
    fileIsOwned: { type: "boolean" },
    src: { type: "string" },
    assetSrc: { type: "string" }, // Original /assets/{uuid} URL for persistence/sync, takes priority over src
    version: { type: "number", default: 1 }, // Used to force a re-resolution
    fitToBox: { default: false },
    moveTheParentNotTheMesh: { default: false },
    resolve: { default: false },
    contentType: { default: null },
    contentSubtype: { default: null },
    animate: { default: true },
    linkedEl: { default: null }, // This is the element of which this is a linked derivative. See linked-media.js
    mediaOptions: {
      default: {},
      parse: v => (typeof v === "object" ? v : JSON.parse(v)),
      stringify: JSON.stringify
    },
    keepLoading: { default: false, type: "boolean" },
    limitScale: { default: false, type: "boolean" },
    targetComponent: { type: "string", default: "media-loader" }, // New target component field
    isCameraTool: { type: "boolean", default: false },
    useFancyLoader: { type: "boolean", default: true }
  },

  init() {
    this.onError = this.onError.bind(this);
    this.onMediaLoaded = this.onMediaLoaded.bind(this);
    this.showLoader = this.showLoader.bind(this);
    this.onAssetMetadataUpdated = this.onAssetMetadataUpdated.bind(this);
    this.loaderId = this.el.uuid || uuidv4();
    this.itemLoadTimeoutId = null;
    this.isNonFancyTracked = false;
    this.assetMetadata = null; // Store metadata from /assets API

    const observerAttributes = {
      src: this.data.src, // Observe 'src'
      ...this.data.mediaOptions, // Observe dynamic mediaOptions
      targetComponent: this.data.targetComponent // Pass target component dynamically
    };

    // Set attributes on the observer component
    this.el.setAttribute("object-to-aframe-observer", observerAttributes);

    // Listen for asset metadata updates
    this.el.sceneEl.addEventListener("asset-metadata-updated", this.onAssetMetadataUpdated);

    // this.el.setAttribute("npc-avatar", { name: "avatar-1"})
    try {
      NAF.utils
        .getNetworkedEntity(this.el)
        .then(networkedEl => {
          this.networkedEl = networkedEl;
        })
        .catch(() => {}); //ignore exception, entity might not be networked
    } catch (e) {
      // NAF may not exist on scene landing page
    }
  },

  // Helper function to convert fraction strings to decimal
  // Examples: "29 5/8" -> 29.625, "21 5/16" -> 21.3125, "5/8" -> 0.625
  parseFractionToDecimal(fractionStr) {
    if (!fractionStr) return null;

    // Match patterns like "29 5/8" or "5/8" or "29.5"
    const mixedFractionMatch = fractionStr.match(/(\d+)\s+(\d+)\/(\d+)/);
    if (mixedFractionMatch) {
      const whole = parseFloat(mixedFractionMatch[1]);
      const numerator = parseFloat(mixedFractionMatch[2]);
      const denominator = parseFloat(mixedFractionMatch[3]);
      return whole + numerator / denominator;
    }

    // Match simple fraction like "5/8"
    const simpleFractionMatch = fractionStr.match(/(\d+)\/(\d+)/);
    if (simpleFractionMatch) {
      const numerator = parseFloat(simpleFractionMatch[1]);
      const denominator = parseFloat(simpleFractionMatch[2]);
      return numerator / denominator;
    }

    // Try to parse as decimal
    const decimal = parseFloat(fractionStr);
    return isNaN(decimal) ? null : decimal;
  },

  async setHoverMenuForHref(href, hideHref) {
    if (await isLocalOrPersistenceAvatarUrl(href)) {
      this.el.setAttribute("hover-menu__hubs-item", {
        template: "#avatar-link-hover-menu",
        isFlat: true
      });
      return;
    }

    if (hideHref) {
      this.el.setAttribute("hover-menu__link", { template: "#transparent-hover-menu", isFlat: true });
    } else {
      this.el.setAttribute("hover-menu__link", { template: "#link-hover-menu", isFlat: true });
    }
  },

  parseDimensionFromString(dimensionsStr, type) {
    if (!dimensionsStr) return "";

    // Museum convention: HEIGHT × WIDTH × DEPTH
    // Format examples:
    // - "75.2 × 54.2 cm (29 5/8 × 21 5/16 in.)" = 75.2 (H) × 54.2 (W)
    // - "24 3/8 x 14 5/8 x 5 1/8 in. (61.9 x 37.1 x 13 cm)" = 61.9 (H) × 37.1 (W) × 13 (D)
    // - "overall: 106.7 × 81.3 cm (42 × 32 in.)" = 42 (H) × 32 (W)
    // Note: First value is HEIGHT, second is WIDTH

    let value = null;

    // First try to extract inches from parentheses (supports fractions like "29 5/8")
    // Pattern: (29 5/8 × 21 5/16 in.) or (29.625 × 21.3125 in.)
    const inchMatch = dimensionsStr.match(/\(([^)]+)\s*in\.?\)/i);

    if (inchMatch) {
      const inchPart = inchMatch[1];
      // Extract dimensions: first is HEIGHT, second is WIDTH, third is DEPTH (optional)
      const dims = inchPart.split(/\s*[×x]\s*/i);

      if (dims.length >= 2) {
        // First value is HEIGHT, second is WIDTH
        const heightStr = dims[0].trim();
        const widthStr = dims[1].trim();
        const depthStr = dims[2] ? dims[2].trim() : null;

        if (type === "height") {
          const parsed = this.parseFractionToDecimal(heightStr);
          value = parsed !== null ? parsed.toString() : null;
        } else if (type === "width") {
          const parsed = this.parseFractionToDecimal(widthStr);
          value = parsed !== null ? parsed.toString() : null;
        } else if (type === "depth" && depthStr) {
          const parsed = this.parseFractionToDecimal(depthStr);
          value = parsed !== null ? parsed.toString() : null;
        }
      }
    }

    // If inches parsing didn't work, try to extract cm from parentheses or main part
    if (!value) {
      // Try cm in parentheses first (e.g., "(61.9 x 37.1 x 13 cm)")
      const cmMatch = dimensionsStr.match(/\(([^)]+)\s*cm\)/i);
      let cmPart = null;

      if (cmMatch) {
        cmPart = cmMatch[1];
      } else {
        // Try main part before parentheses with 'cm' (e.g., "75.2 × 54.2 cm")
        // Look for pattern: number × number cm (before any parentheses)
        const beforeParens = dimensionsStr.split(/\(/)[0];
        const mainCmMatch = beforeParens.match(/([^:]+?)\s*cm/i);
        if (mainCmMatch) {
          cmPart = mainCmMatch[1].trim();
        }
      }

      if (cmPart) {
        // Extract dimensions: first is HEIGHT, second is WIDTH, third is DEPTH (optional)
        const dims = cmPart.split(/\s*[×x]\s*/i);

        if (dims.length >= 2) {
          // First value is HEIGHT, second is WIDTH
          const heightStr = dims[0].trim();
          const widthStr = dims[1].trim();
          const depthStr = dims[2] ? dims[2].trim() : null;

          if (type === "height") {
            const parsed = this.parseFractionToDecimal(heightStr);
            value = parsed !== null ? parsed.toString() : null;
          } else if (type === "width") {
            const parsed = this.parseFractionToDecimal(widthStr);
            value = parsed !== null ? parsed.toString() : null;
          } else if (type === "depth" && depthStr) {
            const parsed = this.parseFractionToDecimal(depthStr);
            value = parsed !== null ? parsed.toString() : null;
          }

          // Convert cm to inches (1 cm = 0.393701 inches)
          if (value) {
            value = (parseFloat(value) * 0.393701).toString();
          }
        }
      }
    }

    return value || "";
  },

  updateScale: (function () {
    const center = new THREE.Vector3();
    const originalMeshMatrix = new THREE.Matrix4();
    const desiredObjectMatrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    const box = new THREE.Box3();
    return function (fitToBox, moveTheParentNotTheMesh) {
      this.el.object3D.updateMatrices();
      const mesh = this.el.getObject3D("mesh");
      mesh.updateMatrices();
      if (moveTheParentNotTheMesh) {
        // Keep the mesh exactly where it is, but move the parent transform such that it aligns with the center of the mesh's bounding box.
        originalMeshMatrix.copy(mesh.matrixWorld);
        computeObjectAABB(mesh, box);
        center.addVectors(box.min, box.max).multiplyScalar(0.5);
        this.el.object3D.matrixWorld.decompose(position, quaternion, scale);
        desiredObjectMatrix.compose(center, quaternion, scale);
        setMatrixWorld(this.el.object3D, desiredObjectMatrix);
        mesh.updateMatrices();
        setMatrixWorld(mesh, originalMeshMatrix);
      } else {
        // Move the mesh such that the center of its bounding box is in the same position as the parent matrix position
        const box = getBox(this.el, mesh);
        let scaleCoefficient = fitToBox ? getScaleCoefficient(0.5, box) : 1;
        const LOADING_SCALE = configs.translation("app-name").toLowerCase().includes("next reality") ? 0.034 : 0.6;
        scaleCoefficient = this.data.limitScale && scaleCoefficient == 1 ? LOADING_SCALE : scaleCoefficient;
        const { min, max } = box;
        center.addVectors(min, max).multiplyScalar(0.5 * scaleCoefficient);
        mesh.scale.multiplyScalar(scaleCoefficient);
        mesh.position.sub(center);
        mesh.matrixNeedsUpdate = true;
      }
    };
  })(),
  is2DObject() {
    return this.data.mediaOptions.check2D && this.el.components["networked"].isMine();
  },
  removeShape(id) {
    if (this.el.getAttribute("shape-helper__" + id)) {
      this.el.removeAttribute("shape-helper__" + id);
    }
  },
  isAssetExternal(assetUrl) {
    if (!assetUrl) return false;

    try {
      const assetOrigin = new URL(assetUrl).origin;
      const currentOrigin = window.location.origin;

      // Asset is internal if it matches either the persistence API or current site origin
      return (
        !isPersistenceUrl(assetUrl, { persistenceApiUrl: configs.PERSISTENCE_API_URL }) && assetOrigin !== currentOrigin
      );
    } catch (e) {
      // If URL parsing fails, assume external for safety
      return true;
    }
  },

  tick(t, dt) {
    if (this.loaderMixer) {
      this.loaderMixer.update(dt / 1000);
    }
  },

  handleLinkedElRemoved(e) {
    if (e.detail.name === "media-loader") {
      this.data.linkedEl.removeEventListener("componentremoved", this.handleLinkedElRemoved);

      // this should be revisited if we ever use media linking for something other than media mirroring UX --
      // right now it is assumed if there is a linkedEl, this is the currently active mirrored media
      closeExistingMediaMirror();
    }
  },

  remove() {
    // Clean up asset metadata update listener
    if (this.el.sceneEl) {
      this.el.sceneEl.removeEventListener("asset-metadata-updated", this.onAssetMetadataUpdated);
    }

    if (this.data.linkedEl) {
      this.data.linkedEl.removeEventListener("componentremoved", this.handleLinkedElRemoved);
    }

    const sfx = this.el.sceneEl.systems["hubs-systems"].soundEffectsSystem;
    if (this.loadingSoundEffect) {
      sfx.stopPositionalAudio(this.loadingSoundEffect);
      this.loadingSoundEffect = null;
    }
    if (this.loadedSoundEffect) {
      sfx.stopPositionalAudio(this.loadedSoundEffect);
      this.loadedSoundEffect = null;
    }

    if (this.isNonFancyTracked) {
      clearTimeout(this.itemLoadTimeoutId);
      notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, false, true);
      this.isNonFancyTracked = false;
    }
    this.itemLoadTimeoutId = null;
    this.assetMetadata = null;
  },

  onError(error) {
    if (!this.data?.src || (typeof this.data.src === "string" && this.data.src.trim() === "")) {
      this.clearLoadingTimeout && this.clearLoadingTimeout();
      return;
    }

    // Emit unified media-error event
    this.el.emit("media-error", {
      src: this.data.src,
      error: error,
      contentType: this.data.contentType
    });

    if (this.isNonFancyTracked) {
      clearTimeout(this.itemLoadTimeoutId);
      this.itemLoadTimeoutId = null;
      notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, false);
      this.isNonFancyTracked = false;
    }

    this.el.removeAttribute("gltf-model-plus");
    this.el.removeAttribute("media-pager");
    this.el.removeAttribute("media-video");
    this.el.removeAttribute("audio-zone-source");
    this.el.removeAttribute("media-pdf");
    this.el.setAttribute("media-image", { src: "error" });
    this.clearLoadingTimeout();
  },

  showLoader() {
    // TODO: If this needed? Disabled for now
    // if (this.el.object3DMap.mesh) {
    //   this.clearLoadingTimeout();
    //   return;
    // }
    const useFancyLoader = !!loadingObject && this.data.useFancyLoader;

    const mesh = useFancyLoader
      ? cloneObject3D(loadingObject.scene)
      : new THREE.Mesh(new THREE.BoxGeometry(), new THREE.MeshBasicMaterial());
    this.el.setObject3D("mesh", mesh);

    this.updateScale(true, false);

    if (useFancyLoader) {
      this.loaderMixer = new THREE.AnimationMixer(mesh);

      this.loadingClip = this.loaderMixer.clipAction(mesh.animations[0]);
      this.loadingScaleClip = this.loaderMixer.clipAction(
        new THREE.AnimationClip(null, 1000, [
          new THREE.VectorKeyframeTrack(".scale", [0, 0.2], [0, 0, 0, mesh.scale.x, mesh.scale.y, mesh.scale.z])
        ])
      );
      setTimeout(() => {
        if (!this.loaderMixer) return; // Animation/loader was stopped early
        this.el.setAttribute("shape-helper__loader", { type: SHAPE.BOX });
      }, 200);

      this.loadingClip.play();
      this.loadingScaleClip.play();
    }

    if (
      this.el.sceneEl.is("entered") &&
      (!this.networkedEl || NAF.utils.isMine(this.networkedEl)) &&
      this.data.playSoundEffect
    ) {
      this.loadingSoundEffect = this.el.sceneEl.systems["hubs-systems"].soundEffectsSystem.playPositionalSoundFollowing(
        SOUND_MEDIA_LOADING,
        this.el.object3D,
        true
      );
    }

    delete this.showLoaderTimeout;
  },

  clearLoadingTimeout() {
    clearTimeout(this.showLoaderTimeout);
    if (this.loadingSoundEffect) {
      this.el.sceneEl.systems["hubs-systems"].soundEffectsSystem.stopPositionalAudio(this.loadingSoundEffect);
      this.loadingSoundEffect = null;
    }
    if (this.loaderMixer) {
      this.loadingClip.stop();
      this.loadingScaleClip.stop();
      delete this.loaderMixer;
      delete this.loadingScaleClip;
      delete this.loadingClip;
    }
    delete this.showLoaderTimeout;
    this.removeShape("loader");
  },

  updateHoverableVisuals: (function () {
    const boundingBox = new THREE.Box3();
    const boundingSphere = new THREE.Sphere();
    return function () {
      const hoverableVisuals = this.el.components["hoverable-visuals"];

      if (hoverableVisuals) {
        if (!this.injectedCustomShaderChunks) {
          this.injectedCustomShaderChunks = true;
          if (!this.el.components["media-video"]) {
            hoverableVisuals.uniforms = injectCustomShaderChunks(this.el.object3D);
          }
        }

        boundingBox.setFromObject(this.el.object3DMap.mesh);
        boundingBox.getBoundingSphere(boundingSphere);
        hoverableVisuals.geometryRadius = boundingSphere.radius / this.el.object3D.scale.y;
      }
    };
  })(),

  onMediaLoaded(physicsShape = null, shouldUpdateScale) {
    const el = this.el;
    this.clearLoadingTimeout();

    if (this.el.sceneEl.is("entered") && this.data.playSoundEffect) {
      this.loadedSoundEffect = this.el.sceneEl.systems["hubs-systems"].soundEffectsSystem.playPositionalSoundFollowing(
        SOUND_MEDIA_LOADED,
        this.el.object3D,
        false
      );
    }

    const finish = () => {
      this.animating = false;

      if (physicsShape) {
        el.setAttribute("shape-helper", {
          type: physicsShape,
          minHalfExtent: 0.04
        });
      }

      this.updateHoverableVisuals();

      if (this.data.linkedEl) {
        this.el.sceneEl.systems["linked-media"].registerLinkage(this.data.linkedEl, this.el);
        this.data.linkedEl.addEventListener("componentremoved", this.handleLinkedElRemoved);
      }

      const entityIsValid = this.el.eid && entityExists(APP.world, el.eid);

      if (entityIsValid) {
        const contentBounds = getBox(this.el, this.el.getObject3D("mesh")).getSize(new THREE.Vector3());
        addComponent(APP.world, MediaContentBounds, el.eid);
        MediaContentBounds.bounds[el.eid].set(contentBounds.toArray());
      }

      el.emit("media-loaded");

      if (entityIsValid) {
        removeComponent(APP.world, MediaLoading, el.eid);
      }

      if (this.isNonFancyTracked) {
        clearTimeout(this.itemLoadTimeoutId);
        this.itemLoadTimeoutId = null;
        notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, false /* not timedOut */);
        this.isNonFancyTracked = false;
      }

      this.loaded = true;
    };

    // Check if artwork from GLB metadata OR from mediaOptions
    const isArtworkFromGLB = this.el.components["gltf-model-plus"]?.model?.userData?.metadata?.isArtwork;
    const isArtworkFromMediaOptions = this.data.mediaOptions?.isArtwork;

    if (isArtworkFromGLB || isArtworkFromMediaOptions) {
      createArtwork(this.el, this.data.src, this.data.mediaOptions);
    }

    // Check if we have artwork metadata from /assets API
    // Extract asset_category from nested artwork structure
    const assetCategory =
      this.assetMetadata?.metadata?.artwork?.asset_category ||
      this.assetMetadata?.artwork?.asset_category ||
      this.assetMetadata?.metadata?.artwork?.metadata?.asset_category ||
      this.assetMetadata?.artwork?.metadata?.asset_category;

    const hasAssetMetadata = this.assetMetadata && this.assetMetadata.metadata?.artwork;
    const isArtworkCategory = assetCategory === "digital-art" || assetCategory === "digital-twin-art";

    if (hasAssetMetadata && isArtworkCategory) {
      // Build asset-metadata component attributes from API data
      const artwork = this.assetMetadata.metadata.artwork;
      const artworkMetadata = artwork.metadata || {};
      const runtimeMetadata = readArtworkRuntimeMetadata(this.assetMetadata);

      // Check if asset is from external platform
      const isExternalAsset = this.isAssetExternal(this.data.src);

      this.el.setAttribute("asset-metadata", {
        // Basic fields
        assetId: this.assetMetadata.uuid || this.assetMetadata.id,
        assetName: this.assetMetadata.title || this.assetMetadata.name,
        assetDescription: this.assetMetadata.description || artwork.description,
        assetUrl: this.assetMetadata.url,
        assetType: this.assetMetadata.type,
        assetCategory: assetCategory,
        isExternalAsset: isExternalAsset,
        previewUrl: this.assetMetadata.previewUrl || this.assetMetadata.preview_url || "",

        // Prefab properties
        prefabSourceId: this.assetMetadata.uuid || this.assetMetadata.id,
        isPrefabInstance: true,

        // Work Information
        artworkYear: artwork.artworkDate || "",
        medium: artwork.medium || "",
        customMedium: artworkMetadata.customMedium || "",
        mediumObject: JSON.stringify(artworkMetadata.medium || null),
        frame: JSON.stringify(artworkMetadata.frame || null),
        dimensionHeight: String(artwork.dimensionsY || ""),
        dimensionWidth: String(artwork.dimensionsX || ""),
        dimensionDepth: String(artwork.dimensionsZ || ""),
        digitizationTechnique: artworkMetadata.techniqueUsed || "",
        currentState: artworkMetadata.currentPhysicalWork || "",
        labels: runtimeMetadata.labels,
        enableAR: runtimeMetadata.enableARTrigger,

        // Artist Information
        artistName: artwork.author || "",
        artistDate: artworkMetadata.authorDate || "",
        artistBio: artworkMetadata.artistBio || "",
        artistStatement: artwork.artistStatement || "",

        // Legal and Rights
        useLicense: artworkMetadata.license || "",
        isOriginal: artworkMetadata.declareRights || false,
        aiCreated: artworkMetadata.workCreatedByAI || false,
        aiTool: artworkMetadata.aiToolUsed || "",
        acceptTerms: artworkMetadata.terms || false
      });
    }

    if (this.data.animate) {
      if (!this.animating) {
        this.animating = true;
        if (shouldUpdateScale) this.updateScale(this.data.fitToBox, this.data.moveTheParentNotTheMesh);
        const mesh = this.el.getObject3D("mesh");
        const scale = { x: 0.001, y: 0.001, z: 0.001 };
        scale.x = mesh.scale.x < scale.x ? mesh.scale.x * 0.001 : scale.x;
        scale.y = mesh.scale.y < scale.y ? mesh.scale.x * 0.001 : scale.y;
        scale.z = mesh.scale.z < scale.z ? mesh.scale.x * 0.001 : scale.z;
        addMeshScaleAnimation(mesh, scale, finish);
      }
    } else {
      if (shouldUpdateScale) this.updateScale(this.data.fitToBox, this.data.moveTheParentNotTheMesh);
      finish();
    }
  },

  onMediaDeferred() {
    this.clearLoadingTimeout();

    if (this.el.getObject3D("mesh")) {
      this.el.removeObject3D("mesh");
    }

    const entityIsValid = this.el.eid && entityExists(APP.world, this.el.eid);

    if (entityIsValid) {
      removeComponent(APP.world, MediaLoading, this.el.eid);
    }

    if (this.isNonFancyTracked) {
      clearTimeout(this.itemLoadTimeoutId);
      this.itemLoadTimeoutId = null;
      notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, false /* not timedOut */);
      this.isNonFancyTracked = false;
    }

    this.loaded = true;
    this.el.emit("media-loaded", { deferred: true });
  },

  onAssetMetadataUpdated(event) {
    const { assetId, assetUuid, updatedMetadata } = event.detail;

    // Check if this entity uses the updated asset
    const matchesById = this.assetMetadata && this.assetMetadata.id === assetId;
    const matchesByUuid = this.assetMetadata && this.assetMetadata.uuid === assetUuid;

    if (!matchesById && !matchesByUuid) {
      return; // This entity doesn't use the updated asset
    }

    // Update the stored metadata
    this.assetMetadata = {
      ...this.assetMetadata,
      ...updatedMetadata,
      // Preserve the id and uuid
      id: this.assetMetadata.id,
      uuid: this.assetMetadata.uuid
    };

    const category = updatedMetadata.asset_category || this.assetMetadata.asset_category;

    // Update or create asset-metadata component
    // asset-metadata will handle updating the artwork component via its own lifecycle
    const hasAssetMetadata = !!this.el.components["asset-metadata"];

    if (category === "digital-art" || category === "digital-twin-art") {
      // Extract artwork data for digital art assets
      const artwork = updatedMetadata.metadata?.artwork || this.assetMetadata.metadata?.artwork || {};
      const artworkMetadata = artwork.metadata || {};
      const runtimeMetadata = readArtworkRuntimeMetadata(this.assetMetadata);

      // Update or create asset-metadata component with new values
      // This will trigger asset-metadata.update() which will update artwork component
      this.el.setAttribute("asset-metadata", {
        // Category
        assetCategory: category,

        // Work Information
        artworkName: artwork.title || "",
        artworkDescription: artwork.description || "",
        artworkYear: artwork.artworkDate || "",
        medium: artwork.medium || "",
        customMedium: artworkMetadata.customMedium || "",
        mediumObject: artworkMetadata.medium ? JSON.stringify(artworkMetadata.medium) : null,
        frame: artworkMetadata.frame ? JSON.stringify(artworkMetadata.frame) : null,
        dimensionHeight: String(artwork.dimensionsY || ""),
        dimensionWidth: String(artwork.dimensionsX || ""),
        dimensionDepth: String(artwork.dimensionsZ || ""),
        digitizationTechnique: artworkMetadata.techniqueUsed || "",
        currentState: artworkMetadata.currentPhysicalWork || "",
        labels: runtimeMetadata.labels,
        enableAR: runtimeMetadata.enableARTrigger,

        // Artist Information
        artistName: artwork.author || "",
        artistDate: artworkMetadata.authorDate || "",
        artistBio: artworkMetadata.artistBio || "",
        artistStatement: artwork.artistStatement || "",

        // Legal and Rights
        isOriginal: artworkMetadata.declareRights || false,
        aiCreated: artworkMetadata.workCreatedByAI || false,
        aiTool: artworkMetadata.aiToolUsed || "",
        acceptTerms: artworkMetadata.terms || false
      });
    } else {
      // For regular file assets, update/create basic metadata or remove if changing from artwork
      if (hasAssetMetadata) {
        // Update existing component to file category
        this.el.setAttribute("asset-metadata", {
          assetCategory: "file",
          artworkName: updatedMetadata.title || "",
          artworkDescription: updatedMetadata.description || ""
        });
      }
      // If no asset-metadata component and category is file, we don't need to create one
    }

    // Emit event to notify that metadata was refreshed
    this.el.emit("asset-metadata-refreshed", { assetId, updatedMetadata });
  },

  refresh() {
    if (this.networkedEl && !NAF.utils.isMine(this.networkedEl) && !NAF.utils.takeOwnership(this.networkedEl)) return;

    // When we refresh, we bump the version to the current timestamp.
    //
    // The only use-case for refresh right now is re-fetching screenshots.
    const refreshSrc = this.data.assetSrc || this.data.src;
    const attrs = { version: Math.floor(Date.now() / 1000) };
    if (refreshSrc && refreshSrc !== this.data.src) {
      attrs.src = refreshSrc;
    }
    this.el.setAttribute("media-loader", attrs);
  },

  async update(oldData, forceLocalRefresh) {
    const { version, src: newSrc, assetSrc, useFancyLoader: newUseFancyLoader } = this.data;
    let currentSrc = assetSrc || newSrc;
    if (!currentSrc || currentSrc.trim() === "about:blank") return;

    const srcChanged = oldData.src !== currentSrc;
    const useFancyLoaderChanged = oldData.useFancyLoader !== newUseFancyLoader;
    if (srcChanged || forceLocalRefresh || oldData.version !== version) {
      if (
        srcChanged &&
        this.networkedEl &&
        !NAF.utils.isMine(this.networkedEl) &&
        typeof console !== "undefined" &&
        console.error
      ) {
        console.error("[media-loader] remote src overwrite", {
          oldSrc: oldData.src,
          newSrc: currentSrc,
          owner: this.networkedEl.components?.networked?.data?.owner
        });
      }
    }

    if (this.isNonFancyTracked && (useFancyLoaderChanged || srcChanged || forceLocalRefresh)) {
      notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, false, true);
      clearTimeout(this.itemLoadTimeoutId);
      this.itemLoadTimeoutId = null;
      this.isNonFancyTracked = false;
    }

    const versionChanged = !!(oldData.version && oldData.version !== version);

    if (versionChanged) {
      this.el.emit("media_refreshing");

      // Don't animate if its a refresh.
      this.data.animate = false;

      // Play the sound effect on a refresh only if we are the owner
      this.data.playSoundEffect = NAF.utils.isMine(this.networkedEl);
    }

    if (forceLocalRefresh) {
      this.el.removeAttribute("gltf-model-plus");
      this.el.removeAttribute("media-pager");
      this.el.removeAttribute("media-video");
      this.el.removeAttribute("audio-zone-source");
      this.el.removeAttribute("media-pdf");
      this.el.removeAttribute("media-image");
    }

    try {
      const pinnableEl = this.data.linkedEl || this.el;
      const pinnableComponent = pinnableEl.components.pinnable;
      const isPinned = pinnableComponent && pinnableComponent.data.pinned;
      const shouldAttemptShowLoader = forceLocalRefresh || srcChanged || useFancyLoaderChanged;
      if (shouldAttemptShowLoader) {
        clearTimeout(this.itemLoadTimeoutId);
        this.itemLoadTimeoutId = null;
        if (!this.data.useFancyLoader) {
          const mediaName = currentSrc.substring(currentSrc.lastIndexOf("/") + 1) || "media asset";
          notificationsCenter.registerNonFancyLoad(this.loaderId, mediaName);
          this.isNonFancyTracked = true;
          this.itemLoadTimeoutId = setTimeout(() => {
            if (this.isNonFancyTracked && this.el.parentNode) {
              notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, true);
              this.isNonFancyTracked = false;
              this.el.emit("media-load-timed-out", { src: currentSrc });
            }
          }, 10000);
          this.clearLoadingTimeout();
        } else {
          if (this.isNonFancyTracked) {
            notificationsCenter.reportNonFancyLoadOutcome(this.loaderId, false, true);
            this.isNonFancyTracked = false;
          }
          if (forceLocalRefresh || (this.data.useFancyLoader && !isPinned)) {
            if (this.showLoaderTimeout) clearTimeout(this.showLoaderTimeout);
            this.showLoaderTimeout = setTimeout(this.showLoader, 0);
          } else {
            this.clearLoadingTimeout();
          }
        }
        if (this.el.eid !== undefined && entityExists(APP.world, this.el.eid)) {
          addComponent(APP.world, MediaLoading, this.el.eid);
        }
      }

      //check if url is an anchor hash e.g. #Spawn_Point_1
      if (currentSrc.charAt(0) === "#") {
        currentSrc =
          this.data.src = `${window.location.origin}${window.location.pathname}${window.location.search}${currentSrc}`;
      }

      // Check if URL matches /assets/{uuid} pattern and resolve it
      if (currentSrc.match(/\/assets\/[a-f0-9-]{36}$/i)) {
        try {
          // Use proxied URL to avoid CORS issues
          const proxiedAssetUrl = proxiedUrlFor(currentSrc);
          const response = await fetch(proxiedAssetUrl);
          if (response.ok) {
            const json = await response.json();

            // Extract actual media URL
            const mediaUrl = json.url || json.data?.url;
            if (mediaUrl) {
              // Update currentSrc and src with resolved URL for loading
              currentSrc = this.data.src = mediaUrl;

              // Extract and store metadata for later use
              const assetData = json.data || json;
              this.assetMetadata = assetData;
            }
          }
        } catch (error) {
          // Failed to resolve /assets URL
          console.error("[MEDIA-LOADER] Error fetching /assets URL:", error);
        }
      }

      // Check if URL matches /met/:id or /nga/:id pattern and resolve it
      // Supports both full URLs (https://example.com/met/123) and paths (/met/123)
      const museumMatch = currentSrc.match(/\/(met|nga)\/([^/?#]+)(?:[/?#]|$)/i);
      let skipResolveUrl = false; // Flag to skip resolveUrl for already-resolved museum URLs
      if (museumMatch) {
        try {
          // Use proxied URL to avoid CORS issues
          const proxiedMuseumUrl = proxiedUrlFor(currentSrc);
          const response = await fetch(proxiedMuseumUrl);
          if (response.ok) {
            const json = await response.json();

            // Extract actual media URL from museum API response
            // API wraps response in {data: {...}} structure
            const museumData = json.data || json;
            const mediaUrl = museumData.url || museumData.image;
            if (mediaUrl) {
              // Update currentSrc and src with resolved URL for loading
              currentSrc = this.data.src = mediaUrl;
              skipResolveUrl = true; // Skip resolveUrl since we already have the final image URL

              // Parse museum metadata into asset-metadata format
              // Museums use HEIGHT × WIDTH × DEPTH convention
              const parsedHeight = this.parseDimensionFromString(museumData.dimensions, "height");
              const parsedWidth = this.parseDimensionFromString(museumData.dimensions, "width");
              const parsedDepth = this.parseDimensionFromString(museumData.dimensions, "depth");

              // For museum-sourced assets we intentionally avoid setting previewUrl / preview_url.
              // These images should never be treated as AR previews, only as display assets.
              this.assetMetadata = {
                id: museumData.id || museumData.objectID,
                uuid: museumData.objectID || museumData.id,
                title: museumData.title,
                name: museumData.title,
                description: museumData.department || "",
                url: mediaUrl,
                type: "image",
                metadata: {
                  artwork: {
                    asset_category: "digital-art",
                    author: museumData.creator || museumData.artistDisplayName,
                    artistStatement: "",
                    medium: museumData.medium ? "Custom" : "",
                    artworkDate: museumData.objectDate || "",
                    dimensionsX: parsedWidth, // Width goes to X
                    dimensionsY: parsedHeight, // Height goes to Y
                    dimensionsZ: parsedDepth, // Depth goes to Z
                    // Pass the full dimensions string as fallback if parsing failed
                    dimensions: !parsedWidth && !parsedHeight ? museumData.dimensions || "" : "",
                    description: museumData.department || "",
                    metadata: {
                      customMedium: museumData.medium || "",
                      museum: museumData.museum,
                      objectURL: museumData.objectURL,
                      department: museumData.department
                    }
                  }
                }
              };
            } else {
              console.warn("[MEDIA-LOADER] No media URL found in museum response");
            }
          } else {
            console.error("[MEDIA-LOADER] Failed to fetch museum data, status:", response.status);
          }
        } catch (error) {
          console.error("[MEDIA-LOADER] Error fetching museum URL:", error);
        }
      }

      let canonicalUrl = currentSrc;
      let canonicalAudioUrl = null; // set non-null only if audio track is separated from video track (eg. 360 video)
      let accessibleUrl = currentSrc;
      let contentType = this.data.contentType;
      let thumbnail;

      const parsedUrl = new URL(currentSrc);

      // We want to resolve and proxy some hubs urls, like rooms and scene links,
      // but want to avoid proxying assets in order for this to work in dev environments
      const isLocalModelAsset =
        (isNonCorsProxyDomain(parsedUrl.hostname) && (guessContentType(currentSrc) || "").startsWith("model/gltf")) ||
        currentSrc.includes(configs["CUSTOM_API_HOST"]) ||
        currentSrc.includes(configs["PERSISTENCE_API_URL"]);

      if (
        this.data.resolve &&
        !skipResolveUrl &&
        !currentSrc.startsWith("data:") &&
        !currentSrc.startsWith("hubs:") &&
        !isLocalModelAsset
      ) {
        if (currentSrc.includes("sketchfab.com/models/") || currentSrc.includes("sketchfab.com/3d-models/")) {
          const persistenceApiUrl = configs.PERSISTENCE_API_URL || "http://localhost:3000";
          const resolveEndpoint = `${persistenceApiUrl}/integrations/sketchfab/resolve?url=${encodeURIComponent(
            currentSrc
          )}`;

          const response = await fetch(resolveEndpoint);
          if (!response.ok) {
            throw new Error(`Persistence API Sketchfab resolution failed: ${response.statusText}`);
          }

          const json = await response.json();
          canonicalUrl = currentSrc;
          accessibleUrl = proxiedUrlFor(json.file_url);
          contentType = "model/gltf+zip";
        } else {
          const is360 = !!(this.data.mediaOptions.projection && this.data.mediaOptions.projection.startsWith("360"));
          const quality = getDefaultResolveQuality(is360);
          const result = await resolveUrl(currentSrc, quality, version, forceLocalRefresh);
          canonicalUrl = result.origin;

          // handle protocol relative urls
          if (canonicalUrl.startsWith("//")) {
            canonicalUrl = location.protocol + canonicalUrl;
          }

          canonicalAudioUrl = result.origin_audio;
          if (canonicalAudioUrl && canonicalAudioUrl.startsWith("//")) {
            canonicalAudioUrl = location.protocol + canonicalAudioUrl;
          }

          contentType = (result.meta && result.meta.expected_content_type) || contentType;
          thumbnail = result.meta && result.meta.thumbnail && proxiedUrlFor(result.meta.thumbnail);
        }
      }

      // todo: we don't need to proxy for many things if the canonical URL has permissive CORS headers
      if (!currentSrc.includes("sketchfab.com/models/") && !currentSrc.includes("sketchfab.com/3d-models/")) {
        accessibleUrl = proxiedUrlFor(canonicalUrl);
      }

      // if the component creator didn't know the content type, we didn't get it from reticulum, and
      // we don't think we can infer it from the extension, we need to make a HEAD request to find it out
      contentType = contentType || guessContentType(canonicalUrl) || (await fetchContentType(accessibleUrl));

      // If the src changed while we were asynchronously resolving the previous one, abort to avoid loading
      // the stale media. This prevents race conditions when the src attribute is updated multiple times in
      // quick succession.
      if (this.data.src !== currentSrc) {
        // Clean up any loading UI we might have shown for the stale request.
        this.clearLoadingTimeout && this.clearLoadingTimeout();
        return;
      }

      // TODO we should probably just never return "application/octet-stream" as expectedContentType, since its not really useful
      if (contentType === "application/octet-stream") {
        contentType = guessContentType(canonicalUrl) || contentType;
      }

      // Some servers treat m3u8 playlists as "audio/x-mpegurl", we always want to treat them as HLS videos
      if (contentType === "audio/x-mpegurl" || contentType === "audio/mpegurl") {
        contentType = "application/vnd.apple.mpegurl";
      }

      // We don't want to emit media_resolved for index updates.
      if (forceLocalRefresh || srcChanged) {
        this.el.emit("media_resolved", { src: currentSrc, raw: accessibleUrl, contentType });
      } else {
        this.el.emit("media_refreshed", { src: currentSrc, raw: accessibleUrl, contentType });
      }

      if (
        contentType.startsWith("video/") ||
        contentType.startsWith("audio/") ||
        contentType.startsWith("application/dash") ||
        AFRAME.utils.material.isHLS(canonicalUrl, contentType)
      ) {
        let linkedVideoTexture, linkedAudioSource, linkedMediaElementAudioSource;
        if (this.data.linkedEl) {
          const linkedMediaVideo = this.data.linkedEl.components["media-video"];

          linkedVideoTexture = linkedMediaVideo.videoTexture;
          linkedAudioSource = linkedMediaVideo.audioSource;
          linkedMediaElementAudioSource = linkedMediaVideo.mediaElementAudioSource;
        }

        const qsTime = parseInt(parsedUrl.searchParams.get("t"));
        const hashTime = parseInt(new URLSearchParams(parsedUrl.hash.substring(1)).get("t"));
        const startTime = hashTime || qsTime || 0;
        this.el.removeAttribute("gltf-model-plus");
        this.el.removeAttribute("media-image");
        this.el.removeAttribute("media-pdf");
        this.el.setAttribute("floaty-object", { reduceAngularFloat: true, releaseGravity: -1 });
        if (this.is2DObject()) {
          window.dispatchEvent(new CustomEvent("open-2D-modal", { detail: this.el }));
        }
        const handleVideoStreamDeferred = () => {
          this.onMediaDeferred();
        };
        const handleVideoLoaded = e => {
          this.el.removeEventListener("video-loaded", handleVideoLoaded);
          this.el.removeEventListener("video-stream-deferred", handleVideoStreamDeferred);
          this.onMediaLoaded(e.detail.projection === "flat" ? SHAPE.BOX : null);
        };

        this.el.addEventListener("video-loaded", handleVideoLoaded);
        this.el.addEventListener("video-stream-deferred", handleVideoStreamDeferred, { once: true });
        this.el.addEventListener("video-error", this.onError, { once: true });

        this.el.setAttribute(
          "media-video",
          Object.assign({}, this.data.mediaOptions, {
            originalSrc: this.data.src,
            src: accessibleUrl,
            audioSrc: canonicalAudioUrl ? proxiedUrlFor(canonicalAudioUrl) : null,
            time: startTime,
            contentType,
            linkedVideoTexture,
            linkedAudioSource,
            linkedMediaElementAudioSource,
            isCameraTool: this.data.isCameraTool
          })
        );
        this.el.setAttribute("audio-zone-source", {});
        if (this.el.components["position-at-border__freeze"]) {
          this.el.setAttribute("position-at-border__freeze", { isFlat: true });
        }
        if (this.el.components["position-at-border__freeze-unprivileged"]) {
          this.el.setAttribute("position-at-border__freeze-unprivileged", { isFlat: true });
        }
      } else if (contentType.startsWith("image")) {
        this.el.removeAttribute("gltf-model-plus");
        this.el.removeAttribute("media-video");
        this.el.removeAttribute("audio-zone-source");
        this.el.removeAttribute("media-pdf");
        this.el.removeAttribute("media-pager");
        if (this.is2DObject()) {
          window.dispatchEvent(new CustomEvent("open-2D-modal", { detail: this.el }));
        }

        this.el.addEventListener(
          "image-loaded",
          async e => {
            this.onMediaLoaded(e.detail.projection === "flat" ? SHAPE.BOX : null);

            if (this.data.contentSubtype === "photo-camera") {
              // this.el.setAttribute("hover-menu__photo", {
              //   template: "#photo-hover-menu",
              //   isFlat: true
              // });
            } else if (this.data.mediaOptions.href) {
              await this.setHoverMenuForHref(this.data.mediaOptions.href, this.data.mediaOptions.hideHref);
            }
          },
          { once: true }
        );
        this.el.addEventListener("image-error", this.onError, { once: true });
        this.el.setAttribute("floaty-object", { reduceAngularFloat: true, releaseGravity: -1 });
        this.el.setAttribute(
          "media-image",
          Object.assign({}, this.data.mediaOptions, {
            src: accessibleUrl,
            version,
            contentType
          })
        );

        if (this.el.components["position-at-border__freeze"]) {
          this.el.setAttribute("position-at-border__freeze", { isFlat: true });
        }
        if (this.el.components["position-at-border__freeze-unprivileged"]) {
          this.el.setAttribute("position-at-border__freeze-unprivileged", { isFlat: true });
        }
      } else if (contentType.startsWith("application/pdf")) {
        this.el.removeAttribute("gltf-model-plus");
        this.el.removeAttribute("media-video");
        this.el.removeAttribute("audio-zone-source");
        this.el.removeAttribute("media-image");
        if (this.is2DObject()) {
          window.dispatchEvent(new CustomEvent("open-2D-modal", { detail: this.el }));
        }

        this.el.setAttribute(
          "media-pdf",
          Object.assign({}, this.data.mediaOptions, {
            src: accessibleUrl,
            contentType
          })
        );
        this.el.setAttribute("media-pager", {});
        this.el.setAttribute("floaty-object", { reduceAngularFloat: true, releaseGravity: -1 });
        this.el.addEventListener(
          "pdf-loaded",
          () => {
            this.clearLoadingTimeout();
            this.onMediaLoaded(SHAPE.BOX);
          },
          { once: true }
        );
        this.el.addEventListener("pdf-error", this.onError, { once: true });

        if (this.el.components["position-at-border__freeze"]) {
          this.el.setAttribute("position-at-border__freeze", { isFlat: true });
        }
        if (this.el.components["position-at-border__freeze-unprivileged"]) {
          this.el.setAttribute("position-at-border__freeze-unprivileged", { isFlat: true });
        }
      } else if (
        contentType.includes("application/octet-stream") ||
        contentType.includes("x-zip-compressed") ||
        contentType.startsWith("model/gltf")
      ) {
        this.el.removeAttribute("media-image");
        this.el.removeAttribute("media-video");
        this.el.removeAttribute("audio-zone-source");
        this.el.removeAttribute("media-pdf");
        this.el.removeAttribute("media-pager");
        this.el.addEventListener(
          "model-loaded",
          async () => {
            if (this.data.mediaOptions.href) {
              await this.setHoverMenuForHref(this.data.mediaOptions.href, this.data.mediaOptions.hideHref);
            }

            this.onMediaLoaded(SHAPE.HULL, true);
            addAnimationComponents(this.el);
          },
          { once: true }
        );
        this.el.addEventListener("model-error", this.onError, { once: true });
        if (Object.prototype.hasOwnProperty.call(this.data.mediaOptions, "applyGravity")) {
          this.el.setAttribute("floaty-object", {
            modifyGravityOnRelease: !this.data.mediaOptions.applyGravity
          });
        }
        if (this.data.keepLoading) return;
        this.el.setAttribute(
          "gltf-model-plus",
          Object.assign({}, this.data.mediaOptions, {
            src: accessibleUrl,
            contentType: contentType,
            inflate: true,
            modelToWorldScale: this.data.fitToBox ? 0.0001 : 1.0
          })
        );
      } else if (contentType.startsWith("text/html")) {
        this.el.removeAttribute("gltf-model-plus");
        this.el.removeAttribute("media-video");
        this.el.removeAttribute("audio-zone-source");
        this.el.removeAttribute("media-pdf");
        this.el.removeAttribute("media-pager");
        this.el.addEventListener(
          "image-loaded",
          async () => {
            const mayChangeScene = this.el.sceneEl.systems.permissions.can("update_hub");

            if (await isLocalOrPersistenceAvatarUrl(currentSrc)) {
              this.el.setAttribute("hover-menu__hubs-item", {
                template: "#avatar-link-hover-menu",
                isFlat: true
              });
            } else if (
              (await isHubsRoomUrl(currentSrc)) ||
              ((await isLocalHubsSceneUrl(currentSrc)) && mayChangeScene)
            ) {
              this.el.setAttribute("hover-menu__hubs-item", {
                template: "#hubs-destination-hover-menu",
                isFlat: true
              });
            } else {
              this.el.setAttribute("hover-menu__link", { template: "#link-hover-menu", isFlat: true });
            }
            this.onMediaLoaded(SHAPE.BOX);
          },
          { once: true }
        );
        this.el.addEventListener("image-error", this.onError, { once: true });
        this.el.setAttribute("floaty-object", { reduceAngularFloat: true, releaseGravity: -1 });
        this.el.setAttribute(
          "media-image",
          Object.assign({}, this.data.mediaOptions, {
            src: thumbnail,
            version,
            contentType: guessContentType(thumbnail) || "image/png"
          })
        );
        if (this.el.components["position-at-border__freeze"]) {
          this.el.setAttribute("position-at-border__freeze", { isFlat: true });
        }
        if (this.el.components["position-at-border__freeze-unprivileged"]) {
          this.el.setAttribute("position-at-border__freeze-unprivileged", { isFlat: true });
        }
      } else {
        throw new Error(`Unsupported content type: ${contentType}`);
      }
    } catch (e) {
      if (!this.data?.src || (typeof this.data.src === "string" && this.data.src.trim() === "")) {
        this.clearLoadingTimeout && this.clearLoadingTimeout();
        return;
      }
      if (this.el.components["position-at-border__freeze"]) {
        this.el.setAttribute("position-at-border__freeze", { isFlat: true });
      }
      if (this.el.components["position-at-border__freeze-unprivileged"]) {
        this.el.setAttribute("position-at-border__freeze-unprivileged", { isFlat: true });
      }
      this.onError(e);
    }
  }
});

AFRAME.registerComponent("media-pager", {
  schema: {
    index: { default: 0 },
    maxIndex: { default: 0 }
  },

  init() {
    this.loaded = false;
    this.onNext = this.onNext.bind(this);
    this.onPrev = this.onPrev.bind(this);
    this.onSnap = this.onSnap.bind(this);
    this.update = this.update.bind(this);

    this.el.setAttribute("hover-menu__pager", { template: "#pager-hover-menu", isFlat: true });
    this.el.components["hover-menu__pager"].getHoverMenu().then(menu => {
      // If we got removed while waiting, do nothing.
      if (!this.el.parentNode) return;

      this.hoverMenu = menu;
      this.nextButton = this.el.querySelector(".next-button [text-button]");
      this.prevButton = this.el.querySelector(".prev-button [text-button]");
      this.snapButton = this.el.querySelector(".snap-button [text-button]");
      this.pageLabel = this.el.querySelector(".page-label");

      this.nextButton.object3D.addEventListener("interact", this.onNext);
      this.prevButton.object3D.addEventListener("interact", this.onPrev);
      this.snapButton.object3D.addEventListener("interact", this.onSnap);

      this.update();
      this.el.emit("pager-loaded");
    });

    NAF.utils
      .getNetworkedEntity(this.el)
      .then(networkedEl => {
        this.networkedEl = networkedEl;
        this.networkedEl.addEventListener("pinned", this.update);
        this.networkedEl.addEventListener("unpinned", this.update);
        window.APP.hubChannel.addEventListener("permissions_updated", this.update);
      })
      .catch(() => {}); //ignore exception, entity might not be networked

    this.el.addEventListener("pdf-loaded", this.update);
  },

  async update(oldData) {
    if (this.networkedEl && NAF.utils.isMine(this.networkedEl)) {
      if (oldData && typeof oldData.index === "number" && oldData.index !== this.data.index) {
        this.el.emit("owned-pager-page-changed");
      }
    }

    if (this.pageLabel) {
      this.pageLabel.setAttribute("text", "value", `${this.data.index + 1}/${this.data.maxIndex + 1}`);
    }

    if (this.prevButton && this.nextButton) {
      const pinnableElement = this.el.components["media-loader"].data.linkedEl || this.el;
      const isPinned = pinnableElement.components.pinnable && pinnableElement.components.pinnable.data.pinned;
      this.prevButton.object3D.visible = this.nextButton.object3D.visible =
        !isPinned || window.APP.hubChannel.can("pin_objects");
    }
  },

  onNext() {
    if (this.networkedEl && !NAF.utils.isMine(this.networkedEl) && !NAF.utils.takeOwnership(this.networkedEl)) return;
    const newIndex = this.data.index === this.data.maxIndex ? 0 : this.data.index + 1;
    this.el.setAttribute("media-pdf", "index", newIndex);
    this.el.setAttribute("media-pager", "index", newIndex);
  },

  onPrev() {
    if (this.networkedEl && !NAF.utils.isMine(this.networkedEl) && !NAF.utils.takeOwnership(this.networkedEl)) return;
    const newIndex = this.data.index === 0 ? this.data.maxIndex : this.data.index - 1;
    this.el.setAttribute("media-pdf", "index", newIndex);
    this.el.setAttribute("media-pager", "index", newIndex);
  },

  onSnap() {
    this.el.emit("pager-snap-clicked");
  },

  remove() {
    if (this.networkedEl) {
      this.networkedEl.removeEventListener("pinned", this.update);
      this.networkedEl.removeEventListener("unpinned", this.update);
    }

    this.nextButton.object3D.removeEventListener("interact", this.onNext);
    this.prevButton.object3D.removeEventListener("interact", this.onPrev);
    this.snapButton.object3D.removeEventListener("interact", this.onSnap);

    window.APP.hubChannel.removeEventListener("permissions_updated", this.update);

    this.el.removeEventListener("pdf-loaded", this.update);
  }
});
